/*
 * State Event Count Report - Controller
 * Angular module: trackerbase-app.views.trackerbase-reports.state-event-count-report
 * State: tbReports.stateEventCount
 * Exposed Controller: StateEventCountReportCtrl
 * Controller in template as: stateEventCountReport
 *
 */

import moment from "moment-timezone";
import Assert from "tbAssert";
import MessageQueue from "../../models/MessageQueue";
import ReportBounds from "./report-models/ReportBounds";

import StateEventReportDataContainer from "./StateEventReportDataContainer";

import FormatTimeZoneNameFilterModule from "../../filters/FormatTimeZoneNameFilter";
import CurrentTimeZoneServiceModule from "../../services/CurrentTimeZoneService";
import StateEventCountReportDataServiceModule from "../../services/StateEventReportDataService";

const viewPageTitle = "TrackerBase: State Event Counts Report";

const reportTimeFormat = "h:mm A";

const datePickerOptions = Object.freeze({
    format: "M/d/yyyy"  /* This kendo date format string and the momentjs format string
                         * in reportBoundsTransferFormat *must* match semantically -
                         * they wiil be used to transfer date/times between kendo objects
                         * and this controller in string format (to avoid any *helpful*
                         * time zone modification by the built-in Date object.
                         **/
});

const datePickerModelOptions = Object.freeze({
    updateOn: "blur default",
    deBounce: Object.freeze({
        blur: 0,
        default: 500
    })
});

const defaultMessageType = "info";
const defaultMessageTimeout = 2000;
const errorMessageType = "danger";

const reportBoundsTransferFormat = "M/D/YYYY";
const reportBoundsPrettyFormat = "MMMM D, YYYY";

const mDocumentSvc = new WeakMap();
const mLogSvc = new WeakMap();
const mCurrentTimeZoneSvc = new WeakMap();
const mStateEventReportDataSvc = new WeakMap();

const mMessageQueue = new WeakMap();

const mReportBounds = new WeakMap();
const mReportGenerated = new WeakMap();
const mStateEventReportDataContainer = new WeakMap();

class StateEventCountReportController {
    constructor($document, $log, CurrentTimeZone, StateEventReportData) {
        Assert.StrictAssert($document != null, "Assertion Failure: Missing Document Service Dependency");
        Assert.StrictAssert($log != null, "Assertion Failure: Missing Log Service Dependency");

        Assert.StrictAssert(CurrentTimeZone != null, "Assertion Failure: Missing Current Time Zone Service Dependency");
        Assert.StrictAssert(StateEventReportData != null, "Assertion Failure: Missing State Event Report Data Service Dependency");

        mDocumentSvc.set(this, $document);
        mLogSvc.set(this, $log);
        mCurrentTimeZoneSvc.set(this, CurrentTimeZone);
        mStateEventReportDataSvc.set(this, StateEventReportData);

        mDocumentSvc.get(this).attr("title", viewPageTitle);

        mMessageQueue.set(this, new MessageQueue(defaultMessageType, defaultMessageTimeout));

        mReportGenerated.set(this, false);
        mStateEventReportDataContainer.set(this, new StateEventReportDataContainer());

        mReportBounds.set(this, new ReportBounds({
            initialTimeZone: CurrentTimeZone.get(),
            transferFormat: reportBoundsTransferFormat,
            prettyFormat: reportBoundsPrettyFormat,
            boundsInitializerFn: (initTimeZone) => {
                let currentMoment = moment().tz(initTimeZone);
                currentMoment.startOf("day");

                let boundsStart = currentMoment.clone();
                let boundsEnd = currentMoment.clone().add(1, "days");

                return {
                    start: boundsStart,
                    end: boundsEnd
                };
            },
            postMomentParseModifierFn: (parsedMoment) => parsedMoment.startOf("day")
        }));
    }

    get messages() {
        return mMessageQueue.get(this).messages;
    }

    get datePickerOptions() {
        return datePickerOptions;
    }

    get datePickerModelOptions(){
        return datePickerModelOptions;
    }

    get reportBounds() {
        return mReportBounds.get(this);
    }

    get stateEventReportData() {
        return mStateEventReportDataContainer.get(this);
    }

    get reportGenerated() {
        return mReportGenerated.get(this);
    }

    removeMessage(msgIdx) {
        mMessageQueue.get(this).removeMessageAtIdx(msgIdx);
    }

    showInCurrentTimeZone(evtMoment) {
        if (moment.isMoment(evtMoment)) {
            return evtMoment.clone().tz(mReportBounds.get(this).reportTimeZone).format(reportTimeFormat);
        }
        else {
            return undefined;
        }
    }

    generateReport(e) {

        mLogSvc.get(this).debug(e);

        if (!this.reportBounds.isValid()) {
            mMessageQueue.get(this).publishMessage(
                "Invalid date values.",
                MessageQueue.timeoutOverrideValue,
                errorMessageType
            );
            return;
        }

        mStateEventReportDataSvc.get(this).getEventCountReportData(
            this.reportBounds.reportStart,
            this.reportBounds.reportEnd
        ).then(
            (reportData) => {
                this.stateEventReportData.clearData();

                Array.prototype.push.apply(
                    this.stateEventReportData.eventReportData,
                    reportData.eventReportData
                );

                this.stateEventReportData.eventTotalCount = reportData.eventTotalCount;
                mReportGenerated.set(this, true);

                mMessageQueue.get(this).publishMessage("Data Loaded Successfully.");

            },
            (errorResponse) => {
                mLogSvc.get(this).error("Error retrieving state event count report data", errorResponse);
                mMessageQueue.get(this).publishMessage(
                    "Error retrieving report data. Please consult the log for further details.",
                    MessageQueue.timeoutOverrideValue,
                    errorMessageType
                );
            }
        );


    }
    

}

StateEventCountReportController.$inject = [
    "$document",
    "$log",
    "CurrentTimeZone",
    "StateEventReportData"
];

StateEventCountReportController.dependencies = [
    "ngSanitize",
    "ngAnimate",
    "ngAria",
    "ui.bootstrap",
    "kendo.directives",
    FormatTimeZoneNameFilterModule,
    CurrentTimeZoneServiceModule,
    StateEventCountReportDataServiceModule
];

StateEventCountReportController.template = require("./StateEventCountReport.tpl.html");

StateEventCountReportController.controllerAs = "stateEventCountReport";

export default StateEventCountReportController;



//////////////////
// WEBPACK FOOTER
// ./src/views/trackerbase-reports/StateEventCountReportController.js
// module id = fqjb
// module chunks = 1